/**
 * <AutoReconcileDialog> — Steel+ feature for matching bank statement uploads
 * against the user's unreconciled transactions.
 *
 * Two phases inside one dialog:
 *   1. Upload    — pick account + CSV/Excel file → POST /api/reconcile/match
 *   2. Review    — show matched pairs with scores + checkboxes → POST /api/reconcile/apply
 *
 * State + side-effects live in `useAutoReconcile`. Each phase is its own
 * small view component (UploadPhase / ReviewPhase). This shell only wires
 * them together — keeps the cyclomatic complexity low and makes each piece
 * independently testable.
 *
 * The backend /reconcile/match + /reconcile/apply endpoints are gated by
 * `require_tier("pro")` so this UI is only shown to Steel/Damascus users; the
 * /reconcile page renders an upsell card for Ember in lieu of this dialog.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import useAutoReconcile from './auto-reconcile/useAutoReconcile';
import UploadPhase from './auto-reconcile/UploadPhase';
import ReviewPhase from './auto-reconcile/ReviewPhase';
import SuccessPhase from './auto-reconcile/SuccessPhase';

const AutoReconcileDialog = ({ open, onOpenChange, accounts, onApplied }) => {
  const ar = useAutoReconcile({
    onApplied,
    onClose: () => onOpenChange(false),
  });

  const handleOpenChange = (next) => {
    if (!next) ar.reset();
    onOpenChange(next);
  };

  // Hide the upload/review header copy when we're celebrating — the
  // success screen owns the whole modal at that point.
  const inSuccess = !!ar.successStats;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white max-w-3xl" data-testid="auto-reconcile-dialog">
        {!inSuccess && (
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#020617]">Auto-reconcile from a bank statement</DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Upload a CSV or Excel statement and we&apos;ll match each row against your unreconciled Forge Ledger transactions.
              Confident matches are pre-selected. Unmatched statement rows can be added as new transactions, or paired by hand to existing Forge transactions if the bank&apos;s amount differs (overtime, irregular spending, etc.).
            </DialogDescription>
          </DialogHeader>
        )}

        {inSuccess ? (
          <SuccessPhase stats={ar.successStats} onDone={ar.dismissSuccess} />
        ) : ar.result ? (
          <ReviewPhase
            result={ar.result}
            selectedIds={ar.selectedIds}
            importIds={ar.importIds}
            pairTags={ar.pairTags}
            applying={ar.applying}
            onToggleMatch={ar.toggleMatch}
            onToggleImport={ar.toggleImport}
            onPairTag={ar.setPairTag}
            onApply={ar.applyMatches}
            onReset={ar.reset}
          />
        ) : (
          <UploadPhase
            accounts={accounts}
            accountId={ar.accountId}
            setAccountId={ar.setAccountId}
            file={ar.file}
            setFile={ar.setFile}
            fileInputRef={ar.fileInputRef}
            matching={ar.matching}
            onMatch={ar.runMatch}
            onCancel={() => handleOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AutoReconcileDialog;
